<template>
    <div>
        <h1 class="page-title">Liste des sociétés ayant des matricules chargés sur la plateforme</h1>
    </div>

    <div class="card">
        <DataTable
            lazy
            removableSort
            paginator
            filterDisplay="row"
            tableStyle="min-width: 50rem"
            showGridlines
            stripedRows
            columnResizeMode="fit"
            :value="tableData"
            :rowHover="true"
            :rows="rows"
            :totalRecords="totalRecords"
            :currentPage="currentPage"
            :loading="isLoading"
            :rowsPerPageOptions="rowsPerPageOptions"
            @page="onPaginate"
            @sort="onSort($event)"
        >
            <template #header>
                <div class="search-container">
                    <span class="p-input-icon-left">
                        <i class="pi pi-search" />
                        <InputText
                            v-model="globalSearch"
                            placeholder="Rechercher une société"
                            @keyup.enter="onGlobalFilterKeyEnter()"
                            @input="onGlobalFilterInput()"
                        />
                    </span>
                </div>
            </template>
            <Column field="id" header="Id Société" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.id }}</span>
                </template>
            </Column>
            <Column field="name" header="Nom" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.name }}</span>
                </template>
            </Column>
            <Column field="matriculeKeyCie" header="Clé Matricule" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.matriculeKeyCie }}</span>
                </template>
            </Column>
            <Column field="accountManager" header="Account Manager" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.accountManager }}</span>
                </template>
            </Column>
            <Column field="walletSc" header="Wallet" :showFilterMenu="false" sortable>
                <template #body="slotProps">
                    <span>{{ slotProps.data.walletSc }}</span>
                </template>
            </Column>
            <Column headerStyle="width: 12em;">
                <template #header>
                    <div>Actions</div>
                </template>
                <template #body="slotProps">
                    <Button
                        @click="redirectToCompanyMatriculeList(slotProps.data)"
                        type="button"
                        title="Voir"
                        icon="pi pi-eye"
                        size="small"
                        class="p-button-eye"
                    >
                    </Button>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from "vue";
import DataTable from "primevue/datatable";
import {fetchCompaniesWithUserMatricule} from "@/_services/company/company";
import {ICompanyWithUserMatricule} from "@/models/companyMatricule/ICompanyWithUserMatricule";
import {useCompanyMatriculeStore} from "@/stores/companyMatricule/companyMatriculeStore";
import InputText from "primevue/inputtext";
import Column from "primevue/column";
import Button from "primevue/button";
import { useRouter } from 'vue-router';

const router = useRouter();
const store = useCompanyMatriculeStore();
const isLoading = ref<boolean>(true);
const tableData = ref<ICompanyWithUserMatricule[]>([]);
const rows = ref<number>(10);
const totalRecords = ref<number>(0);
const page = ref<number>(1);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100];
const currentPage = ref<number>(0);
const globalSearch = ref<string>("");
const modeSortable = ref<boolean>(false);
const defaultsSort = {
    fieldLabel: "order[id]",
    fieldValue: "desc",
};

onMounted(async () => {
    if (isLoading.value) {
        showSpinner();
    }
    
    await populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort);
});

const populateDatatable = async (
    itemsPerPage: number,
    page: number,
    globalSearch?:string,
    sort?: { fieldLabel: string; fieldValue: string; },
    filters?: Record<string, string | number>,
) => {
    const [isLoadingDt, totalRecordsDt, tableDataDt] = await fetchCompaniesWithUserMatricule(itemsPerPage, page, globalSearch, sort, filters);
    isLoading.value = isLoadingDt;
    totalRecords.value = totalRecordsDt;
    tableData.value = tableDataDt;
    hideSpinner();
};

const onPaginate = async (e) => {
    rows.value = e.rows;
    showSpinner();
    await populateDatatable(e.rows, e.page + 1, globalSearch.value, defaultsSort);
};

const onGlobalFilterKeyEnter = () => {
    if (globalSearch.value.trim() != "") {
        showSpinner();
        populateDatatable(rows.value, page.value, globalSearch.value);
    }
};

const onGlobalFilterInput = () => {
    if (globalSearch.value === "") {
        showSpinner();
        populateDatatable(rows.value, page.value);
    }
};

const onSort = (e) => {
    showSpinner();
    if (e.sortField) {
        const sort = {
            fieldLabel: "order[" + e.sortField + "]",
            fieldValue: e.sortOrder === 1 ? "asc" : "desc",
        };
        modeSortable.value = true;
        populateDatatable(rows.value, page.value, globalSearch.value, sort);
    } else {
        modeSortable.value = false;
        populateDatatable(rows.value, page.value, globalSearch.value, defaultsSort);
    }
};

const showSpinner = () => {
    isLoading.value = true;
};

const redirectToCompanyMatriculeList = (company: ICompanyWithUserMatricule) => {
    store.setSelectedCompany({
        uuid: company.uuid,
        name: company.name,
    });
    
    router.push({
        name: 'userMatriculesByCompany',
        params: { uuid: company.uuid }
    });
};

const hideSpinner = () => {
    isLoading.value = false;
};
</script>

<style scoped>
</style>